import React, { useRef, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import {
  useCustomer,
  useNavigation,
  useConfig,
  useCookie
} from 'msp-integrations'
import RequestPasswordModal from '@organisms/RequestPassword/RequestPasswordModal'
import AlertBox from '@molecules/AlertBox/AlertBox'
import Field from '@molecules/Field/Field'
import { Button } from '@atoms/Button'
import Spinner from '@atoms/Spinner/Spinner'
import Icons from '@atoms/Icons/Icons'
import CustomLinkComponent from '@atoms/CustomLink/CustomLinkComponent'
import getLoginFields from './LoginForm.fields'
import { LoginFormProps } from './LoginForm.types'

const LoginForm = ({
  className,
  redirectTo,
  inFlyout = false,
  onLoginSuccessful,
  setLoadingState,
  isDarkMode = false,
  origin = 'registration'
}: LoginFormProps) => {
  const {
    API_ERROR_MESSAGE,
    theme: {
      font,
      mspComponents: { loginForm }
    }
  } = useConfig()
  const { eventTracking } = useCookie()
  const [requestPassword, setRequestPassword] = useState<boolean>(false)
  const { formatMessage } = useIntl()
  const fieldConfig = getLoginFields()
  const navigate = useNavigation()
  const customer = useCustomer()
  const form = useForm()
  const location = useLocation<any>()
  const alertBoxRef = useRef<typeof AlertBox>()
  const [alertMessage, setAlertMessage] = useState<string>('')

  const submitForm = async (data: { email: string; password: string }) => {
    if (setLoadingState) {
      setLoadingState(true)
    }
    try {
      await customer.login(data)
      eventTracking('login_succeeded', { loggedIn: 1, pageType: origin })

      if (onLoginSuccessful) {
        await onLoginSuccessful()
      } else {
        if (redirectTo) {
          navigate(redirectTo)
        } else if (location?.state?.directedFrom) {
          navigate(location.state.directedFrom)
        }
      }
    } catch (error: any) {
      const message = /invalid credentials/i.test(error.message)
        ? formatMessage({
            defaultMessage: 'Incorrect username or password, please try again.',
            id: 'msp.login_form.error.incorrect_username_or_password'
          })
        : formatMessage(API_ERROR_MESSAGE)

      eventTracking('login_failed', { reason: message, pageType: origin })
      setAlertMessage(message)
      alertBoxRef?.current?.showAlert()
    } finally {
      if (setLoadingState) {
        setLoadingState(false)
      }
    }
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <AlertBox
        ref={alertBoxRef}
        type={'error'}
        title={alertMessage}
        autoCloseTime={4000}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
      />
      <form
        onSubmit={form.handleSubmit(submitForm)}
        id={'login-form'}
        data-testid={'login-form-test'}
      >
        <Field
          className={'w-full'}
          control={form.control}
          config={fieldConfig.email}
          error={form.errors?.email}
          isDisabled={form.formState.isSubmitting}
          additionalProps={{ isDarkMode: isDarkMode }}
        />
        <Field
          className={'w-full'}
          control={form.control}
          config={fieldConfig.password}
          error={form.errors?.password}
          isDisabled={form.formState.isSubmitting}
          additionalProps={{ isDarkMode: isDarkMode }}
        />

        <button
          type='button'
          className='leading-3'
          onClick={() => setRequestPassword(true)}
        >
          <p
            className={`${font.regular} ${loginForm.tracking} ${
              loginForm.fontSize
            } underline ${isDarkMode ? 'text-white' : 'text-menuGray'}`}
          >
            {formatMessage({
              id: 'msp.login_form.button.forgot_password',
              defaultMessage: 'Forgot password?'
            })}
          </p>
        </button>

        <Button
          className={`mt-5 w-full ${inFlyout ? 'w-auto' : 'w-full'}`}
          onClick={() => form.clearErrors(['email', 'password'])}
          iconName={!form.formState.isSubmitting ? 'ArrowRight' : ''}
          iconPosition='right'
          isDisabled={form.formState.isSubmitting}
          isLoading={form.formState.isSubmitting}
          isSubmit
          loadingContent={<Spinner />}
          isDarkMode={isDarkMode}
        >
          {formatMessage({
            defaultMessage: 'Login',
            id: 'msp.login_form.button.login'
          })}
        </Button>

        {inFlyout && (
          <CustomLinkComponent
            href={'/registration'}
            className={'mt-6 w-min whitespace-nowrap tracking-wide'}
            colorScheme={isDarkMode ? 'secondary' : undefined}
          >
            {formatMessage({
              defaultMessage: 'Register now',
              id: 'msp.login_form.button.register_now'
            })}
          </CustomLinkComponent>
        )}
      </form>
      <RequestPasswordModal
        isOpen={requestPassword}
        setOpen={setRequestPassword}
        hideBodyOverflow={false}
      />
    </div>
  )
}

export default LoginForm
